/**
 * SetPasswordPrompt — R29
 * A small non-blocking banner + modal that appears when the authenticated user
 * has `must_set_password === true` (typically set after a guest signup).
 * Mounted globally under AuthGate so it shows on any protected page.
 */
import React, { useState } from "react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";

export const SetPasswordPrompt = () => {
  const { user, setSession, token } = useAuth();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  // R29 iter6 fix: 'Later' persists a dismissal so the banner actually goes away for the session
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem('setPasswordPromptDismissed') === '1'; } catch { return false; }
  });

  // Show only if user is authenticated AND flagged to set password AND not dismissed this session
  if (!user || !user.must_set_password || dismissed) return null;

  const submit = async () => {
    if (password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (password !== confirm) { toast.error("Passwords do not match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/set-password", { password });
      // Update local user object so the prompt disappears
      const nextUser = { ...user, must_set_password: false };
      setSession(token, nextUser);
      toast.success("Password saved — you can now sign in with email + password.");
      setOpen(false);
      setPassword(""); setConfirm("");
    } catch (err) {
      const msg = err?.response?.data?.error?.message
                || err?.response?.data?.detail
                || err?.message
                || "Could not save password.";
      toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <>
      {/* Persistent banner */}
      <div data-testid="set-password-banner" className="fixed bottom-4 right-4 z-40 max-w-sm bg-emerald-600/95 backdrop-blur border border-emerald-400/30 text-white rounded-xl shadow-2xl p-4">
        <div className="text-sm font-semibold mb-1">Add a password for faster sign-in</div>
        <div className="text-xs text-emerald-50/90 mb-3">Next time you can log in with just your email and password — no code required.</div>
        <div className="flex gap-2">
          <button
            data-testid="set-password-open-btn"
            onClick={() => setOpen(true)}
            className="flex-1 bg-white text-emerald-700 hover:bg-emerald-50 text-sm font-medium py-2 rounded-lg"
          >Set password</button>
          <button
            data-testid="set-password-later-btn"
            onClick={() => { try { sessionStorage.setItem('setPasswordPromptDismissed','1'); } catch (_e) {} setDismissed(true); setOpen(false); }}
            className="px-3 text-xs text-emerald-50/80 hover:text-white"
            title="I'll do it later"
          >Later</button>
        </div>
      </div>

      {open && (
        <div
          data-testid="set-password-modal"
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
        >
          <div className="w-full max-w-md bg-slate-900 border border-slate-700 rounded-2xl p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Set your password</h3>
              <button data-testid="set-password-close" onClick={() => setOpen(false)} className="text-slate-400 hover:text-white">✕</button>
            </div>
            <p className="text-xs text-slate-400 mb-4">Hi {user.name || user.email}, add a password so future logins don't require an email code.</p>
            <label className="block text-xs text-slate-400">New password</label>
            <input
              data-testid="set-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full mt-1 mb-3 px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white"
              placeholder="At least 8 characters"
              autoComplete="new-password"
            />
            <label className="block text-xs text-slate-400">Confirm password</label>
            <input
              data-testid="set-password-confirm"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="w-full mt-1 mb-4 px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white"
              placeholder="Retype the password"
              autoComplete="new-password"
            />
            <button
              data-testid="set-password-submit"
              onClick={submit}
              disabled={busy}
              className="w-full h-11 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white font-medium disabled:opacity-50"
            >{busy ? "Saving..." : "Save password"}</button>
          </div>
        </div>
      )}
    </>
  );
};

export default SetPasswordPrompt;
